// macadamia-app/src/macadamia-core/components/widgets/composite-text-widget/JurisprudenceModal.tsx
import React, { useState, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from './../../../../context/AuthContext';
import { apiClient } from './../../../../utils/apiClient';
import { useTokens } from './../../../../context/TokenContext';
import LinkIcon from '../../../icons/Link';
import XCircle from '../../../icons/XCircle';
import ChevronRight from '../../../icons/ChevronRight';
import FollowButton from './../../../../features/jurisprudences/components/FollowButton';
import JurisprudenceSimilairesTab from './../../../../features/jurisprudences/components/JurisprudenceSimilairesTab';
import AppliedTextCard from './AppliedTextCard';
import LegifranceModal from './legifrance/LegifranceModal';

interface decisionLink {
  title: string;
  number: string;
  description: string;
  theme: string[];
  url: string;
}

interface JurisprudenceModalProps {
  isVisible: boolean;
  onClose: () => void;
  isLoading: boolean;
  error?: string | null;
  reference: string;
  jurisdiction: string;
  chamber: string;
  number: string;
  ecli: string;
  formation: string;
  publication: string[];
  decisionDate: string;
  solution: string;
  summary: string;
  themes: string[];
  text: string;
  zones?: {
    introduction?: { start: number; end: number }[];
    expose?: { start: number; end: number }[];
    moyens?: { start: number; end: number }[];
    motivations?: { start: number; end: number }[];
    dispositif?: { start: number; end: number }[];
  };
  visa?: { title: string }[];
  contested?: {
    date: string;
    title: string;
    jurisdiction: string;
    chamber: string;
    solution: string;
    number: string;
  };
  rapprochements?: decisionLink[];
  userRole: string | null;
  jurisprudenceFollowState?: boolean;
  onJurisprudenceFollowChange?: (reference: string, isFollowed: boolean) => void;
}

const generateJurisprudenceTitle = (jurisdiction: string, location: string, number: string, decisionDate: string) => {
  if (jurisdiction === "Cour d'appel") {
    const locationPart = location ? `de ${location.replace(/^Cour d'appel de /, '')}` : '';
    return `${decisionDate || 'Date inconnue'} - Cour d'appel ${locationPart} ${number ? `- RG n° ${number}` : ''}`.trim();
  }
  return `${decisionDate || 'Date inconnue'} - ${jurisdiction || 'Juridiction inconnue'} ${number ? `n° ${number}` : ''}`.trim();
};

const extractLinkFromTitle = (title: string): string | null => {
  const linkRegex = /href="([^"]*)"/;
  const match = linkRegex.exec(title);
  return match ? match[1] : null;
};

/**
 * Normalise le texte de jurisprudence en éliminant les sauts de ligne excessifs
 * tout en préservant la structure des paragraphes
 */
const normalizeJurisprudenceText = (text: string): string => {
  return text
    // Étape 1: Normaliser les sauts de ligne Windows/Unix
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')

    // Étape 2: Remplacer les sauts de ligne simples par des espaces
    // (sauf s'ils sont précédés de ponctuation forte ou suivis d'une majuscule)
    .replace(/([^.!?:;\n])\n(?=[a-zàâäéèêëïîôöùûüÿœæ])/g, '$1 ')

    // Étape 3: Conserver les paragraphes (2+ sauts de ligne)
    .replace(/\n{2,}/g, '\n\n')

    // Étape 4: Nettoyer les balises <br> excessives
    .replace(/(<br\s*\/?>){2,}/gi, '<br><br>')

    // Étape 5: Nettoyer les espaces multiples
    .replace(/ {2,}/g, ' ')

    // Étape 6: Trim
    .trim();
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
      active
        ? 'border-mcdm-secondary-500 text-mcdm-secondary-500 dark:border-mcdm-secondary-400 dark:text-mcdm-secondary-400'
        : 'border-transparent text-mcdm-third-400 hover:text-mcdm-third-500 dark:text-mcdm-primary-200 dark:hover:text-mcdm-primary-100'
    }`}
  >
    {children}
  </button>
);

interface ExtractedLegalReference {
  originalText: string;
  type: 'article' | 'section' | 'code' | 'unknown';
  source?: 'visa' | 'ai-full-text' | 'manual';
  found: boolean;
  articleId?: string;
  codeId?: string;
  sectionId?: string;
  articleNum?: string;
  codeTitle?: string;
  sectionTitle?: string;
  confidence?: number;
  enrichmentDate?: string;
}

const JurisprudenceModal: React.FC<JurisprudenceModalProps> = ({
  isVisible,
  onClose,
  isLoading,
  error,
  reference,
  jurisdiction,
  chamber,
  number,
  ecli,
  formation,
  publication = [],
  decisionDate,
  solution,
  summary,
  themes = [],
  text,
  zones,
  visa = [],
  contested,
  rapprochements = [],
  userRole,
  jurisprudenceFollowState,
  onJurisprudenceFollowChange
}) => {
  useAuth();
  const { balance, deductTokens } = useTokens();
  const [aiSummary, setAiSummary] = useState<string>('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState<boolean>(false);
  const [showSummaryBlock, setShowSummaryBlock] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('texte');

  // États pour les textes de loi enrichis
  const [appliedTexts, setAppliedTexts] = useState<ExtractedLegalReference[]>([]);
  const [isLoadingTexts, setIsLoadingTexts] = useState<boolean>(false);
  const [isAnalyzingFullText, setIsAnalyzingFullText] = useState<boolean>(false);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const [isFullTextAnalyzed, setIsFullTextAnalyzed] = useState<boolean>(false);
  const [isFullTextAnalysisInProgress, setIsFullTextAnalysisInProgress] = useState<boolean>(false);
  const [showAllAppliedTexts, setShowAllAppliedTexts] = useState<boolean>(false);

  // États pour le modal Legifrance
  const [isLegifranceModalVisible, setIsLegifranceModalVisible] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<string | undefined>(undefined);
  const [selectedCodeId, setSelectedCodeId] = useState<string | undefined>(undefined);
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(undefined);
  
  // État pour gérer les sections ouvertes/fermées (par défaut tout ouvert)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    introduction: true,
    expose: true,
    moyens: true,
    motivations: true,
    dispositif: true,
  });

  // État pour gérer la largeur du modal avec drag
  const [modalWidth, setModalWidth] = useState<number>(420); // Largeur par défaut
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [dragStartWidth, setDragStartWidth] = useState<number>(0);
  const [isResizing, setIsResizing] = useState<boolean>(false); // Flag pour bloquer le onClick
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [previousWidth, setPreviousWidth] = useState<number>(modalWidth);
  const [clickStartX, setClickStartX] = useState<number>(0);

  // Limites de largeur
  const MIN_WIDTH = 320;
  const MAX_WIDTH = 1000;

  const title = generateJurisprudenceTitle(jurisdiction, '', number, decisionDate);

  // Fonction helper pour afficher "Non précisé" si la valeur est vide
  const displayValue = (value: string | undefined | null) => {
    if (!value || value.trim() === '') {
      return <span className="text-mcdm-third-100 dark:text-mcdm-primary-500">Non précisé</span>;
    }
    return value;
  };

  // Fonction pour toggle maximiser/restaurer
  const handleClick = () => {
    if (isMaximized) {
      setModalWidth(previousWidth);
      setIsMaximized(false);
    } else {
      setPreviousWidth(modalWidth);
      setModalWidth(MAX_WIDTH);
      setIsMaximized(true);
    }
  };

  // Gestion du début du drag
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Empêcher la propagation immédiate
    setIsDragging(true);
    setIsResizing(true); // Activer le flag de protection
    setDragStartX(e.clientX);
    setClickStartX(e.clientX); // Tracker la position initiale pour détecter le clic
    setDragStartWidth(modalWidth);
  };

  // Gestion du drag en cours
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculer le delta (négatif car on tire vers la gauche pour agrandir)
      const delta = dragStartX - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth + delta));
      setModalWidth(newWidth);

      // Si on a bougé, désactiver l'état maximisé
      if (Math.abs(delta) > 5) {
        setIsMaximized(false);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);

      // Différencier clic vs drag : si mouvement < 5px, c'est un clic
      const deltaMovement = Math.abs(clickStartX - e.clientX);
      if (deltaMovement < 5) {
        handleClick();
      }

      // Désactiver le flag après un court délai pour éviter le onClick de l'overlay
      setTimeout(() => {
        setIsResizing(false);
      }, 50);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartWidth, clickStartX, handleClick]);

  // Fonction pour interpréter le markdown maison
  const renderMarkdown = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let currentIndex = 0;
    let key = 0;

    // Regex pour capturer le markdown: **texte**, *texte*
    const markdownRegex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let match;

    while ((match = markdownRegex.exec(text)) !== null) {
      // Ajouter le texte avant le match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }

      // Déterminer le type de markdown
      if (match[1].startsWith('***')) {
        // Gras + Italique: ***texte***
        parts.push(<strong key={key++}><em>{match[2]}</em></strong>);
      } else if (match[1].startsWith('**')) {
        // Gras: **texte**
        parts.push(<strong key={key++}>{match[3]}</strong>);
      } else if (match[1].startsWith('*')) {
        // Italique: *texte*
        parts.push(<em key={key++}>{match[4]}</em>);
      }

      currentIndex = match.index + match[0].length;
    }

    // Ajouter le reste du texte
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts;
  };

  const handleGenerateSummary = useCallback(async () => {
    if (!text || isGeneratingSummary) return;

    setIsGeneratingSummary(true);
    setShowSummaryBlock(true);

    try {
      const response = await apiClient('/ai/generate-summary', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });

      // Gérer spécifiquement l'erreur 402 (tokens insuffisants)
      if (response.status === 402) {
        const errorData = await response.json();
        setAiSummary(
          `️ Tokens insuffisants\n\n` +
          `Balance actuelle : ${errorData.balance || 0} tokens\n` +
          `Requis : ${errorData.required || 'N/A'} tokens\n\n` +
          `Créditez votre compte pour continuer.`
        );
        return;
      }

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      setAiSummary(data.summary);

      // Déduire les tokens après succès (optimistic update)
      if (data.tokenUsage && data.tokenUsage.cost) {
        deductTokens(data.tokenUsage.cost);
      }
    } catch (error) {
      console.error('Erreur lors de la génération du résumé:', error);
      setAiSummary('Erreur lors de la génération du résumé. Veuillez réessayer.');
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [text, isGeneratingSummary, deductTokens]);

  /**
   * Enrichit les textes de loi appliqués à la demande
   */
  const enrichAppliedTexts = useCallback(async () => {
    if (!reference) return;

    setEnrichmentError(null);

    try {
      const response = await apiClient(`/jurisprudence-metadata/${reference}/enrich-applied-texts`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data.applied_law_texts) {
        setAppliedTexts(data.data.applied_law_texts);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (error) {
      console.error('Erreur lors de l\'enrichissement des textes:', error);
      setEnrichmentError('Impossible d\'enrichir les textes de loi');
      setAppliedTexts([]);
    }
  }, [reference]);

  /**
   * Enrichit les textes de loi à partir du texte intégral (AI)
   */
  const enrichFromFullText = useCallback(async () => {
    if (!reference) return;

    setEnrichmentError(null);

    try {
      const response = await apiClient(`/jurisprudence-metadata/${reference}/enrich-from-full-text`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data.applied_law_texts) {
        setAppliedTexts(data.data.applied_law_texts);
        setIsFullTextAnalyzed(true);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (error) {
      console.error('Erreur analyse texte intégral:', error);
      setEnrichmentError('Impossible d\'analyser le texte intégral');
    }
  }, [reference]);

  /**
   * Charge les métadonnées et enrichit si nécessaire
   * Lance automatiquement les deux enrichissements en parallèle
   */
  useEffect(() => {
    let isMounted = true;

    const loadAppliedTexts = async () => {
      if (!isVisible || !reference) return;

      // Mettre en loading immédiatement pour éviter le flash de "Liens non disponibles"
      setIsLoadingTexts(true);

      try {
        // Tenter de charger les métadonnées existantes
        const metadataResponse = await apiClient(`/jurisprudence-metadata/${reference}`);

        if (metadataResponse.ok) {
          const metadataData = await metadataResponse.json();

          if (metadataData.success && metadataData.data?.applied_law_texts?.length > 0) {
            // Les textes enrichis existent déjà - les afficher immédiatement
            if (isMounted) {
              setAppliedTexts(metadataData.data.applied_law_texts);
              setIsLoadingTexts(false); // Arrêter le loading, les données sont en cache
              const isAnalyzed = metadataData.data.enrichment_status?.full_text_analyzed || false;
              const isInProgress = metadataData.data.enrichment_status?.full_text_analysis_in_progress || false;
              setIsFullTextAnalyzed(isAnalyzed);
              setIsFullTextAnalysisInProgress(isInProgress);

              // Si l'analyse IA n'a pas encore été faite, la lancer en arrière-plan
              if (!isAnalyzed) {
                setIsAnalyzingFullText(true);
                enrichFromFullText().finally(() => {
                  if (isMounted) {
                    setIsAnalyzingFullText(false);
                  }
                });
              }
            }
            return;
          }
        }

        // Pas de métadonnées ou métadonnées vides → Lancer les deux enrichissements en parallèle
        if (isMounted) {
          setIsLoadingTexts(true);
          try {
            await Promise.all([
              enrichAppliedTexts(),     // Enrichir depuis Judilibre (source: 'visa')
              enrichFromFullText()       // Enrichir depuis IA (source: 'ai-full-text')
            ]);
          } finally {
            setIsLoadingTexts(false);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des textes appliqués:', error);
        if (isMounted) {
          setEnrichmentError('Erreur lors du chargement');
          setIsLoadingTexts(false);
        }
      }
    };

    loadAppliedTexts();

    return () => {
      isMounted = false;
    };
  }, [isVisible, reference, enrichAppliedTexts, enrichFromFullText]);

  /**
   * Ouvre le modal Legifrance avec les détails d'un article
   */
  const handleOpenLegifranceModal = useCallback((articleId: string, codeId?: string, sectionId?: string) => {
    setSelectedArticleId(articleId);
    setSelectedCodeId(codeId);
    setSelectedSectionId(sectionId);
    setIsLegifranceModalVisible(true);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'texte':
        return (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Texte intégral:</strong></p>
              <button
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary || !text || balance <= 0}
                title={balance <= 0 ? "Tokens insuffisants - créditez votre compte" : ""}
                className={`px-3 py-1 rounded ${
                  isGeneratingSummary || !text || balance <= 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-mcdm-secondary-400 text-white hover:bg-mcdm-secondary-500'
                }`}
              >
                {isGeneratingSummary ? 'Génération...' : 'Résumé IA'}
              </button>
            </div>
            {showSummaryBlock && (
              <div className="mt-4 p-3 rounded" style={{ backgroundColor: 'var(--green-400)' }}>
                <h4 className="font-bold mb-2 text-white">Résumé :</h4>
                {isGeneratingSummary ? (
                  <div className="flex flex-col items-center">
                    <img
                      src="/gifs/Wizard.gif"
                      alt="Chargement"
                      style={{ height: '80px', width: '80px' }}
                    />
                    <p className="text-white mt-2">Patientez...</p>
                  </div>
                ) : (
                  <p className='text-white whitespace-pre-wrap'>{renderMarkdown(aiSummary)}</p>
                )}
              </div>
            )}
            {zones ? (
              // Affichage structuré avec sections
              <div className="text-sm mt-4">
                {[
                  { key: 'introduction', label: 'Introduction', ranges: zones.introduction },
                  { key: 'expose', label: 'Exposé du litige', ranges: zones.expose },
                  { key: 'moyens', label: 'Moyens', ranges: zones.moyens },
                  { key: 'motivations', label: 'Motivation', ranges: zones.motivations },
                  { key: 'dispositif', label: 'Dispositif', ranges: zones.dispositif }
                ].map((section) => {
                  if (!section.ranges || section.ranges.length === 0) return null;

                  const isExpanded = expandedSections[section.key];

                  return (
                    <div key={section.key} className="mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
                      <button
                        onClick={() => setExpandedSections(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
                        className="w-full flex items-center justify-between p-2 bg-gray-50 dark:bg-mcdm-third-400 hover:bg-gray-100 dark:hover:bg-mcdm-third-300 rounded transition-colors"
                      >
                        <h3 className="font-bold text-base text-mcdm-third-500 dark:text-mcdm-primary-100">{section.label}</h3>
                        <div className={`text-gray-600 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                          <ChevronRight width="16px" height="16px" />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="mt-2 pl-2">
                          {section.ranges.map((range, idx) => {
                            const sectionText = text.substring(range.start, range.end);
                            return (
                              <div
                                key={idx}
                                className="break-words mb-2 leading-relaxed text-mcdm-third-500 dark:text-mcdm-primary-100"
                                style={{ whiteSpace: 'pre-line' }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(normalizeJurisprudenceText(sectionText)) }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Fallback : affichage simple sans zones
              <div
                className="text-sm mt-4 break-words leading-relaxed text-mcdm-third-500 dark:text-mcdm-primary-100"
                style={{ whiteSpace: 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text ? normalizeJurisprudenceText(text) : 'Aucun texte disponible.') }}
              />
            )}
          </div>
        );
      case 'rapprochements':
        return (
          <div className="-mx-6">
            <JurisprudenceSimilairesTab
              jurisprudenceId={reference}
              themes={themes}
              isFullTextAnalyzed={isFullTextAnalyzed}
              isFullTextAnalysisInProgress={isFullTextAnalysisInProgress}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-20 z-30 transition-opacity duration-300"
      onClick={(e) => {
        // Ne fermer que si on ne vient PAS de redimensionner
        if (!isResizing) {
          onClose();
        }
      }}
    >
      <div
        className="fixed inset-y-0 right-0 bg-mcdm-primary-100 dark:bg-mcdm-third-500 shadow-lg z-40 overflow-y-auto"
        style={{
          width: `${modalWidth}px`,
          transition: isDragging ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle de redimensionnement sur le bord gauche */}
        <div
          onMouseDown={handleMouseDown}
          className={`absolute top-0 left-0 bottom-0 w-1 cursor-ew-resize hover:bg-mcdm-secondary-400 transition-colors duration-200 group ${
            isDragging ? 'bg-mcdm-secondary-500' : 'bg-transparent'
          }`}
          style={{ zIndex: 50 }}
        />

        {/* Indicateur visuel fixé au centre de l'écran */}
        <div
          onMouseDown={handleMouseDown}
          className={`fixed w-1 h-16 bg-mcdm-secondary-400 dark:bg-mcdm-secondary-300 rounded-full cursor-ew-resize transition-opacity duration-200 ${
            isDragging ? 'opacity-100' : 'opacity-40 hover:opacity-100'
          }`}
          style={{
            top: '50vh',
            right: `${modalWidth}px`,
            transform: 'translate(50%, -50%)',
            zIndex: 60,
            transition: isDragging ? 'opacity 0.2s' : 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s',
          }}
        >
          {/* Points décoratifs */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1">
            <div className="w-1 h-1 bg-white rounded-full"></div>
            <div className="w-1 h-1 bg-white rounded-full"></div>
            <div className="w-1 h-1 bg-white rounded-full"></div>
          </div>
        </div>

        <div className="p-6 text-sm">
          {/* Header avec boutons à gauche et fermeture à droite */}
          <div className="flex justify-between items-center mb-4">
            {/* Boutons d'action à gauche */}
            <div className="flex items-center gap-2">
              {!isLoading && !error && (
                <>
                  <FollowButton
                    jurisprudenceId={reference}
                    initialFollowState={jurisprudenceFollowState}
                    onFollowChange={onJurisprudenceFollowChange}
                  />
                  <a
                    href={`https://www.courdecassation.fr/decision/${reference}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                    title="Voir sur le site de la Cour de cassation"
                  >
                    <LinkIcon width="16px" height="16px" />
                    Cour de cassation
                  </a>
                </>
              )}
            </div>

            {/* Bouton fermer à droite */}
            <button
              onClick={onClose}
              className="p-1 hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-400 rounded-full transition-colors duration-200"
              title="Fermer"
            >
              <svg
                className="w-5 h-5 text-mcdm-third-500 dark:text-mcdm-primary-100"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-full mb-4"></div>
              <h2 className="text-xl font-bold mb-2">Détails de la jurisprudence</h2>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-2/3"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-1/2"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-3/4"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-2/3"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-1/2"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-3/4"></div>
              <div className="h-4 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-2/5"></div>
              <div className="h-40 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 rounded w-full"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="w-16 h-16 mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                Erreur de chargement
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-2">
                {error}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 text-center mb-6">
                Le service Judilibre est parfois encombré, n'hésitez pas à ré-essayer dans quelques minutes.
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-mcdm-secondary-400 hover:bg-mcdm-secondary-500 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">{title}</h2>

              {userRole === 'admin' && (
                <>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Référence :</strong> {reference}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>ECLI :</strong> {displayValue(ecli)}</p>
                </>
              )}
              {contested && (
                <>
                  <hr className="my-4" />
                  <h3 className="text-lg font-bold mb-2 text-mcdm-third-500 dark:text-mcdm-primary-100">Décision Contestée</h3>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Numéro :</strong> {displayValue(contested.number)}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Date :</strong> {contested.date ? new Date(contested.date).toLocaleDateString() : <span className="text-mcdm-third-100 dark:text-mcdm-primary-500">Non précisé</span>}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Juridiction :</strong> {displayValue(contested.jurisdiction)}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Chambre :</strong> {displayValue(contested.chamber)}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Solution :</strong> {displayValue(contested.solution)}</p>
                  <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Intitulé :</strong> {displayValue(contested.title)}</p>
                </>
              )}

              {/* Section Textes appliqués - Toujours affichée */}
              <>
                <hr className="my-4" />
                <h3 className="text-lg font-bold mb-3 text-mcdm-third-500 dark:text-mcdm-primary-100">Textes appliqués</h3>

                {/* État de chargement unifié */}
                {isLoadingTexts && (
                  <div className="flex items-center gap-2 text-xs text-mcdm-secondary-500 dark:text-mcdm-secondary-400 py-2 px-3 rounded-lg bg-mcdm-secondary-50 dark:bg-mcdm-secondary-500/5 mb-3 border border-mcdm-secondary-200 dark:border-mcdm-secondary-500/20">
                    <div className="animate-spin h-3 w-3 border-2 border-mcdm-secondary-500 border-t-transparent rounded-full"></div>
                    <span>Récupération des textes appliqués...</span>
                  </div>
                )}

                {/* Textes enrichis disponibles */}
                {!isLoadingTexts && appliedTexts.length > 0 && (
                  <div className="mb-4">
                    {/* Conteneur avec dégradé optionnel */}
                    <div
                      className={`space-y-3 relative ${
                        appliedTexts.length > 4 && !showAllAppliedTexts ? 'pb-6' : ''
                      }`}
                    >
                      {/* Afficher les 4 premiers éléments ou tous les éléments */}
                      {appliedTexts.slice(0, showAllAppliedTexts ? appliedTexts.length : 4).map((ref, index) => (
                        <AppliedTextCard
                          key={index}
                          reference={ref}
                          onOpenLegifranceModal={handleOpenLegifranceModal}
                        />
                      ))}

                      {/* Dégradé en bas de la liste tronquée */}
                      {appliedTexts.length > 4 && !showAllAppliedTexts && (
                        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent dark:from-mcdm-third-500 dark:to-transparent pointer-events-none"></div>
                      )}
                    </div>

                    {/* Bouton "Voir plus" / "Voir moins" */}
                    {appliedTexts.length > 4 && (
                      <button
                        onClick={() => setShowAllAppliedTexts(!showAllAppliedTexts)}
                        className="w-full mt-3 px-3 py-2 text-sm font-medium text-mcdm-secondary-500 hover:text-mcdm-secondary-600 dark:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        {showAllAppliedTexts ? (
                          <>
                            <span>Voir moins</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            <span>Voir plus ({appliedTexts.length - 4} supplémentaire{appliedTexts.length - 4 > 1 ? 's' : ''})</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    )}

                    {/* Indicateur secondaire : Analyse IA en arrière-plan */}
                    {isAnalyzingFullText && (
                      <div className="flex items-center gap-2 text-xs text-mcdm-secondary-500 dark:text-mcdm-secondary-400 py-2 px-3 rounded-lg bg-mcdm-secondary-50 dark:bg-mcdm-secondary-500/5 mt-3 border border-mcdm-secondary-200 dark:border-mcdm-secondary-500/20">
                        <div className="animate-spin h-3 w-3 border-2 border-mcdm-secondary-500 border-t-transparent rounded-full"></div>
                        <span>Recherche de textes supplémentaires en cours...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Aucun texte disponible */}
                {!isLoadingTexts && appliedTexts.length === 0 && visa.length === 0 && (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm">Aucun texte juridique trouvé pour cette décision.</p>
                  </div>
                )}

                {/* Fallback vers visa bruts (si des visa existent mais pas d'enrichissement ou erreur) */}
                {!isLoadingTexts && appliedTexts.length === 0 && visa.length > 0 && (
                  <div className="space-y-4 mb-3">
                    {enrichmentError && (
                      <div className="text-orange-600 dark:text-orange-400 text-xs mb-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                        ️ Impossible d'enrichir les textes. Affichage des données brutes.
                      </div>
                    )}
                    {visa.map((item, index) => {
                      const link = extractLinkFromTitle(item.title);
                      return link && link !== "#" ? (
                        <a
                          key={index}
                          href={link}
                          className="block bg-primary-100 border border-gray-200 rounded-lg p-4 flex justify-between items-center transition hover:border-gray-400 hover:shadow-md dark:text-mcdm-third-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="flex-grow text-mcdm-third-500 dark:text-mcdm-primary-100" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title) }}></span>
                          <div className="flex-shrink-0 ml-4">
                            <LinkIcon
                              width="24px"
                              height="24px"
                              className="text-mcdm-third-500 dark:text-mcdm-primary-100"
                            />
                          </div>
                        </a>
                      ) : (
                        <div
                          key={index}
                          className="block border border-gray-200 bg-mcdm-primary-100 dark:bg-mcdm-third-500 rounded-lg p-4"
                        >
                          <span className="flex-grow text-mcdm-third-500 dark:text-mcdm-primary-100" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title) }}></span>
                          <div className="flex items-center mt-2">
                            <XCircle width="24px" height="24px" className="text-red-500" />
                            <p className="text-red-500 ml-2">Lien non disponible</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>

              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Juridiction :</strong> {displayValue(jurisdiction)}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Chambre :</strong> {displayValue(chamber)}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Numéro :</strong> {displayValue(number)}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Formation :</strong> {displayValue(formation)}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100">
                <strong>Publication :</strong>{' '}
                {publication.length > 0 ? publication.join(', ') : <span className="text-mcdm-third-100 dark:text-mcdm-primary-500">Non précisé</span>}
              </p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Date :</strong> {decisionDate ? new Date(decisionDate).toLocaleDateString() : <span className="text-mcdm-third-100 dark:text-mcdm-primary-500">Non précisé</span>}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Solution :</strong> {displayValue(solution)}</p>
              <p className="text-mcdm-third-500 dark:text-mcdm-primary-100"><strong>Résumé :</strong> {displayValue(summary)}</p>

              <div className="flex flex-wrap items-center text-mcdm-third-500 dark:text-mcdm-primary-100">
                <strong className="mr-2">Thèmes :</strong>
                {themes.length > 0 ? themes.map((theme, index) => (
                  <span
                    key={index}
                    className="bg-mcdm-primary-200 dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100 px-4 py-1 rounded-full mr-2 mb-1 mt-1 text-sm"
                  >
                    {theme}
                  </span>
                )) : 'Aucun thème'}
              </div>

              <hr className="my-4" />

              {/* Onglets */}
              <div className="flex border-b border-mcdm-third-200 dark:border-mcdm-third-600">
                <TabButton active={activeTab === 'texte'} onClick={() => setActiveTab('texte')}>
                  Texte intégral
                </TabButton>
                <TabButton active={activeTab === 'rapprochements'} onClick={() => setActiveTab('rapprochements')}>
                  Rapprochements
                </TabButton>
              </div>

              {renderTabContent()}
            </>
          )}
        </div>
      </div>

      {/* Modal Legifrance pour les détails complets d'un article */}
      <LegifranceModal
        isVisible={isLegifranceModalVisible}
        onClose={() => setIsLegifranceModalVisible(false)}
        isLoading={false}
        articleId={selectedArticleId}
        codeId={selectedCodeId}
        sectionId={selectedSectionId}
      />
    </div>
  );
};

export default JurisprudenceModal;